/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { EApiLanguageCode } from '@datatype/iyagigo/api-type';
import { ETheme } from '@datatype/type';
import { useCreateExpressionBatchMutation } from '@hook/admin/useApi';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import TextButton from '@design-system/text-button';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  initialSourceKey?: string | null;
  initialLanguageCode?: EApiLanguageCode | null;
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: ${color.MODAL_COLOR};
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  gap: 16px;
  width: 100%;
  max-width: 380px;
  background-color: ${color.WHITE_1};
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${color.SURFACE_SHADOW};
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
`;

const Notice = styled.div`
  background-color: ${color.BROWN_4};
  color: ${color.BROWN_1};
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
`;

const ExpressionCreateModal: React.FC<IProps> = ({ isOpen, onClose, initialSourceKey, initialLanguageCode }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [requested, setRequested] = useState<boolean>(false);

  const createMutation = useCreateExpressionBatchMutation();

  const handleClose = () => {
    setErrorMessage(null);
    setRequested(false);
    onClose();
  };

  const handleSubmit = () => {
    setErrorMessage(null);
    if (!initialSourceKey || !initialLanguageCode) {
      setErrorMessage('소스를 먼저 선택해주세요.');
      return;
    }
    // 표현 생성은 STT + LLM 흐름이라 오래 걸림 (수 분 단위).
    // 응답을 기다리지 않고 fire-and-forget — 요청만 던지고 "요청되었습니다" 상태로 전환.
    createMutation.mutate({
      languageCode: initialLanguageCode,
      createCount: 1,
      authenticExpressionSourceKey: initialSourceKey,
    });
    setRequested(true);
  };

  if (!isOpen) return null;

  return (
    <Backdrop onClick={handleClose}>
      <Card onClick={(ev) => ev.stopPropagation()}>
        <CloseButton>
          <IconBlock
            icon='close'
            design={{
              icon: { size: 'medium', color: color.BLACK_4 },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleClose }}
          />
        </CloseButton>
        {requested ? (
          <>
            <TextBlock
              text='요청되었습니다'
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
            />
            <Notice>
              생성 작업이 백그라운드로 진행 중입니다. STT + LLM 처리로 수 분 정도 걸려요. 결과는 잠시 후 목록을 새로고침해서 확인하세요.
            </Notice>
          </>
        ) : (
          <>
            <TextBlock
              text='표현을 만드시겠어요?'
              design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
            />
            <Notice>
              생성 작업은 STT + LLM 처리로 수 분 정도 소요됩니다. 요청 후에는 백그라운드로 진행되니 기다리지 마세요.
            </Notice>
            {errorMessage && (
              <TextBlock
                text={errorMessage}
                design={{ text: { size: 'small', color: color.RED_2 } }}
              />
            )}
            <ButtonRow>
              <TextButton
                text='아니오'
                theme={ETheme.GRAY}
                kind='outlined'
                width='100px'
                handleClick={handleClose}
              />
              <TextButton text='예' width='100px' handleClick={handleSubmit} />
            </ButtonRow>
          </>
        )}
      </Card>
    </Backdrop>
  );
};

export default ExpressionCreateModal;
